import { OtherCostTypeSelect } from 'components/EditOtherForm/OtherCostTypeSelect';
import { ControlledTextField, ControlNumberField } from 'components/Form';
import { ReadField } from 'components/ReadField';
import type { BimContractOtherCostData, SectionEnum } from 'gql/graphql';
import { OtherCostCalculationTypeEnum, WorkOrderStatusEnum } from 'gql/graphql';
import { OTHER_TABLE_EDIT_NAME } from 'pages/Project/BimProject/WorkOrder/types';
import { validateLimitCost } from 'pages/Project/Common/hooks/prepareLimitsData';
import { cnMixSpace, GridItem } from 'uikit';

import cn from 'classnames';
import React, { useEffect, useMemo } from 'react';

import type { TEditOtherModalCommonLimitsProps } from '../../types';

import Styles from './BimEditOtherCommon.module.css';

const MARGIN_BOTTOM = cnMixSpace({ mB: 's' });

export const BimEditOtherCommon = <T extends BimContractOtherCostData>(props: TEditOtherModalCommonLimitsProps<T>) => {
    const {
        control,
        otherData,
        setValue,
        statusWorkOrder,
        isTooltipHidden,
        forceUseRationaleField = false,
        orderBimRules,
    } = props;
    const isCalculatingTypePercent = otherData?.calculatingType === OtherCostCalculationTypeEnum.Percent;
    const calculatingTypeText = isCalculatingTypePercent ? '%' : 'Расчет';
    const isInProgress = useMemo(() => statusWorkOrder === WorkOrderStatusEnum.InProgress, [statusWorkOrder]);

    const selectSection = useMemo(
        () =>
            otherData?.section &&
            OTHER_TABLE_EDIT_NAME[otherData?.section as Exclude<SectionEnum, SectionEnum.LimitedCost>],
        [otherData?.section],
    );

    useEffect(() => {
        setValue('name', otherData?.name);
        setValue('calculatingTypeValue', otherData?.calculatingTypeValue);
        setValue('rationale', otherData?.rationale);
        setValue('otherCostType', otherData?.catalogOtherCostType);
    }, [
        otherData?.calculatingTypeValue,
        otherData?.catalogOtherCostType,
        otherData?.name,
        otherData?.rationale,
        setValue,
    ]);

    const otherCostTypeDisabled = useMemo(
        () => !orderBimRules?.canEdit || Boolean(otherData?.isGlobalOtherCost && otherData?.catalogOtherCostType),
        [orderBimRules?.canEdit, otherData?.catalogOtherCostType, otherData?.isGlobalOtherCost],
    );

    return (
        <>
            <GridItem>
                <ReadField
                    label="Способ создания"
                    value={otherData?.isGlobalOtherCost ? 'Из справочника методик расчета' : 'Вручную'}
                    classWrapper={Styles.wrapperText}
                    classFirstText={cn(Styles.leftBlock, cnMixSpace({ mR: 's' }))}
                />
            </GridItem>
            {otherData?.isGlobalOtherCost ? (
                <GridItem className={Styles.wrapperText}>
                    <ReadField
                        label="Наименование"
                        value={otherData?.name}
                        classWrapper={Styles.wrapperText}
                        classFirstText={cn(Styles.leftBlock, cnMixSpace({ mR: 's' }))}
                    />
                </GridItem>
            ) : (
                <GridItem className={MARGIN_BOTTOM}>
                    <ControlledTextField
                        name="name"
                        label="Наименование"
                        control={control}
                        type="textarea"
                        cols={50}
                        rows={5}
                        rules={{
                            required: 'Обязательное поле',
                            maxLength: {
                                value: 255,
                                message: 'не более 255 символов',
                            },
                        }}
                        isTooltipHidden={isTooltipHidden}
                        disabled={!orderBimRules?.canEdit}
                    />
                </GridItem>
            )}
            <GridItem className={Styles.wrapperText}>
                <ReadField
                    label="Способ расчета"
                    value={calculatingTypeText}
                    classWrapper={Styles.wrapperText}
                    classFirstText={cn(Styles.leftBlock, cnMixSpace({ mR: 's' }))}
                />
            </GridItem>
            {isCalculatingTypePercent && (
                <GridItem className={MARGIN_BOTTOM}>
                    <ControlNumberField
                        name="calculatingTypeValue"
                        label="Величина"
                        labelPosition="left"
                        control={control}
                        size="s"
                        decimalScale={3}
                        style={{ width: '100%' }}
                        rules={{
                            validate: validateLimitCost({
                                isSelected: true,
                                checkNegativePercent: true,
                                checkZero: false,
                            }),
                        }}
                        isTooltipHidden={isTooltipHidden}
                        disabled={!orderBimRules?.canEdit}
                    />
                </GridItem>
            )}
            <GridItem className={cn(Styles.wrapperText, cnMixSpace({ mB: 'm' }))}>
                <ReadField
                    label="Раздел"
                    value={selectSection}
                    classWrapper={Styles.wrapperText}
                    classFirstText={cn(Styles.leftBlock, cnMixSpace({ mR: 's' }))}
                />
            </GridItem>
            <GridItem className={MARGIN_BOTTOM}>
                <OtherCostTypeSelect
                    isTooltipHidden={isTooltipHidden}
                    isDisabled={otherCostTypeDisabled}
                    control={control}
                    id={otherData?.id}
                />
            </GridItem>
            {(isInProgress || forceUseRationaleField) && (
                <GridItem>
                    <ControlledTextField
                        name="rationale"
                        label="Обоснование"
                        control={control}
                        placeholder="Обоснование"
                        type="textarea"
                        cols={30}
                        rows={3}
                        rules={{ required: 'Обязательное поле' }}
                        isTooltipHidden={isTooltipHidden}
                        disabled={!orderBimRules?.canEdit}
                    />
                </GridItem>
            )}
        </>
    );
};

BimEditOtherCommon.displayName = 'BimEditOtherCommon';
